import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DashboardStats {
  runsThisMonth: number;
  avgClarityScore: number;
  totalProjects: number;
}

export function useDashboardStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    runsThisMonth: 0,
    avgClarityScore: 0,
    totalProjects: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats({ runsThisMonth: 0, avgClarityScore: 0, totalProjects: 0 });
      setLoading(false);
      return;
    }

    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [projectsResult, runsResult, monthlyRunsResult] = await Promise.all([
        supabase
          .from("projects")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("runs")
          .select("clarity_score")
          .eq("user_id", user.id)
          .eq("status", "succeeded")
          .not("clarity_score", "is", null),
        supabase
          .from("runs")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .gte("created_at", startOfMonth),
      ]);

      const totalProjects = projectsResult.count || 0;
      const runsThisMonth = monthlyRunsResult.count || 0;

      const runs = runsResult.data || [];
      const avgClarityScore = runs.length > 0
        ? Math.round(runs.reduce((sum, r) => sum + (Number(r.clarity_score) || 0), 0) / runs.length)
        : 0;

      setStats({ runsThisMonth, avgClarityScore, totalProjects });
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
}
